/**
 * Candidate Comparison Page Component
 * Displays side-by-side comparison of multiple candidates or sessions with radar charts and metrics
 * Supports two modes:
 * 1. Candidates mode: Compare up to 4 different candidates
 * 2. Sessions mode: Compare up to 4 sessions for a single candidate
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Users, Clock, ChevronDown } from 'lucide-react';
import {
    RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
    ResponsiveContainer, Legend, Tooltip
} from 'recharts';
import clsx from 'clsx';
import axios from 'axios';
import { AppHeader } from './AppHeader';
import { API_BASE, getAuthHeaders } from '../../utils/api';

type ComparisonMode = 'candidates' | 'sessions';

interface Candidate {
    id: string;
    name: string;
    email: string;
    status: string;
    average_score: number | null;
    position_applied?: string;
    image_url?: string;
    user_id?: string;
    session_id?: string;
    simulation_title?: string;
    created_at?: string;
    completed_at?: string;
}

interface SkillEvaluation {
    skill: string;
    score: number;
    trend?: 'improving' | 'stable' | 'needs_attention';
    evaluation_count?: number;
    positive_actions?: number;
    negative_actions?: number;
}

interface AggregatedSkillCluster {
    skill: string;
    score: number;
    objective_count: number;
    objectives?: string[];
}

interface CandidateEvaluation {
    candidate_id?: string;
    session_id?: string;
    overall_score?: number;
    simulation_summary?: {
        total_turns: number;
        completed_objectives: number;
        total_objectives: number;
        overall_progress: number;
    };
    semantic_skill_evaluation?: SkillEvaluation[];
    skill_evaluations?: SkillEvaluation[];
    aggregated_skill_clusters?: AggregatedSkillCluster[];
}

interface CandidateSession {
    id: string;
    session_id: string;
    simulation_title: string;
    completed_at: string | null;
    score: number | null;
    status: string;
}

interface CandidateComparisonPageProps {
    candidateIds?: string[];
}

const CHART_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ec4899'];
const CHART_COLOR_CLASSES = [
    { bg: 'bg-emerald-500', ring: 'ring-emerald-500', text: 'text-emerald-600' },
    { bg: 'bg-blue-500', ring: 'ring-blue-500', text: 'text-blue-600' },
    { bg: 'bg-amber-500', ring: 'ring-amber-500', text: 'text-amber-600' },
    { bg: 'bg-pink-500', ring: 'ring-pink-500', text: 'text-pink-600' },
];

const getSkillEvaluations = (evaluation: CandidateEvaluation | undefined): SkillEvaluation[] => {
    if (!evaluation) return [];
    const skills = evaluation.semantic_skill_evaluation || evaluation.skill_evaluations || [];
    return skills.filter((skill) => {
        const skillLower = skill.skill.toLowerCase();
        return !skillLower.includes('simulation goal') && 
               !skillLower.includes('primary goal') &&
               !skillLower.includes('main goal');
    });
};

export function CandidateComparisonPage({
    candidateIds: propCandidateIds
}: CandidateComparisonPageProps) {
    const [searchParams] = useSearchParams();
    
    // Get candidate IDs from props or URL params
    const getInitialCandidateIds = (): string[] => {
        if (propCandidateIds && propCandidateIds.length > 0) {
            return propCandidateIds.slice(0, 4);
        }
        const idsParam = searchParams.get('ids');
        if (idsParam) {
            return idsParam.split(',').filter(Boolean).slice(0, 4);
        }
        return [];
    };
    
    const [comparisonMode, setComparisonMode] = useState<ComparisonMode>('candidates');
    const [candidates, setCandidates] = useState<Candidate[]>([]);
    const [evaluations, setEvaluations] = useState<Record<string, CandidateEvaluation>>({});
    const [loading, setLoading] = useState(true);
    const [selectedCandidates, setSelectedCandidates] = useState<string[]>(getInitialCandidateIds());
    
    // Session comparison state
    const [selectedCandidateForSessions, setSelectedCandidateForSessions] = useState<string | null>(null);
    const [candidateSessions, setCandidateSessions] = useState<CandidateSession[]>([]);
    const [selectedSessions, setSelectedSessions] = useState<string[]>([]);
    const [sessionEvaluations, setSessionEvaluations] = useState<Record<string, CandidateEvaluation>>({});
    const [loadingSessions, setLoadingSessions] = useState(false);

    useEffect(() => {
        loadCandidatesAndEvaluations();
    }, []);

    useEffect(() => {
        if (selectedCandidateForSessions) {
            loadSessionsForCandidate(selectedCandidateForSessions);
        }
    }, [selectedCandidateForSessions]);

    // Load evaluations for selected candidates when selection changes
    useEffect(() => {
        if (comparisonMode === 'candidates' && selectedCandidates.length > 0) {
            const loadMissingEvaluations = async () => {
                const authHeaders = await getAuthHeaders();
                for (const id of selectedCandidates) {
                    if (!evaluations[id]) {
                        try {
                            const response = await axios.get(`${API_BASE}/candidates/${id}/evaluation`, { headers: authHeaders });
                            if (response.data.data) {
                                setEvaluations(prev => ({ ...prev, [id]: response.data.data }));
                            }
                        } catch (err) {
                            console.error(`Failed to load evaluation for candidate ${id}:`, err);
                        }
                    }
                }
            };
            loadMissingEvaluations();
        }
    }, [selectedCandidates, comparisonMode]);

    // Load evaluations for selected sessions when selection changes
    useEffect(() => {
        if (comparisonMode === 'sessions' && selectedSessions.length > 0) {
            const loadMissingSessionEvaluations = async () => {
                const authHeaders = await getAuthHeaders();
                for (const sessionId of selectedSessions) {
                    if (!sessionEvaluations[sessionId]) {
                        try {
                            const response = await axios.get(`${API_BASE}/candidates/${sessionId}/evaluation`, { headers: authHeaders });
                            if (response.data.data) {
                                setSessionEvaluations(prev => ({ ...prev, [sessionId]: response.data.data }));
                            }
                        } catch (err) {
                            console.error(`Failed to load evaluation for session ${sessionId}:`, err);
                        }
                    }
                }
            };
            loadMissingSessionEvaluations();
        }
    }, [selectedSessions, comparisonMode]);

    const loadCandidatesAndEvaluations = async () => {
        try {
            const authHeaders = await getAuthHeaders();
            const candidatesResponse = await axios.get(`${API_BASE}/candidates`, { headers: authHeaders });
            const allCandidates = candidatesResponse.data.data.candidates || [];
            setCandidates(allCandidates.filter((c: Candidate) => c.status === 'completed'));

            const initialIds = getInitialCandidateIds();
            if (initialIds.length > 0) {
                const evalPromises = initialIds.map(async (id) => {
                    try {
                        const response = await axios.get(`${API_BASE}/candidates/${id}/evaluation`, { headers: authHeaders });
                        return { id, evaluation: response.data.data };
                    } catch (err) {
                        console.error(`Failed to load evaluation for candidate ${id}:`, err);
                        return { id, evaluation: null };
                    }
                });

                const evalResults = await Promise.all(evalPromises);
                const evalMap: Record<string, CandidateEvaluation> = {};
                evalResults.forEach(({ id, evaluation }) => {
                    if (evaluation) {
                        evalMap[id] = evaluation;
                    }
                });
                setEvaluations(evalMap);
            }
        } catch (err) {
            console.error('Failed to load comparison data:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadSessionsForCandidate = async (candidateId: string) => {
        setLoadingSessions(true);
        setSelectedSessions([]);
        setSessionEvaluations({});
        
        try {
            const authHeaders = await getAuthHeaders();
            const candidatesResponse = await axios.get(`${API_BASE}/candidates`, { headers: authHeaders });
            const allCandidates = candidatesResponse.data.data.candidates || [];
            
            const selectedCandidate = allCandidates.find((c: Candidate) => c.id === candidateId);
            if (!selectedCandidate) {
                setCandidateSessions([]);
                return;
            }
            
            const candidateUserId = selectedCandidate.user_id || selectedCandidate.id;
            const sessions = allCandidates
                .filter((c: Candidate) => {
                    const cUserId = c.user_id || c.id;
                    return cUserId === candidateUserId && c.status === 'completed';
                })
                .map((c: Candidate) => ({
                    id: c.id,
                    session_id: c.session_id || c.id,
                    simulation_title: c.simulation_title || 'Simulation',
                    completed_at: c.completed_at || c.created_at || null,
                    score: c.average_score,
                    status: c.status
                }));
            
            setCandidateSessions(sessions);
        } catch (err) {
            console.error('Failed to load sessions for candidate:', err);
            setCandidateSessions([]);
        } finally {
            setLoadingSessions(false);
        }
    };

    const loadEvaluationsForSessions = async (sessionIds: string[]) => {
        try {
            const authHeaders = await getAuthHeaders();
            const evalPromises = sessionIds.map(async (id) => {
                try {
                    const response = await axios.get(`${API_BASE}/candidates/${id}/evaluation`, { headers: authHeaders });
                    return { id, evaluation: response.data.data };
                } catch (err) {
                    console.error(`Failed to load evaluation for session ${id}:`, err);
                    return { id, evaluation: null };
                }
            });

            const evalResults = await Promise.all(evalPromises);
            const evalMap: Record<string, CandidateEvaluation> = {};
            evalResults.forEach(({ id, evaluation }) => {
                if (evaluation) {
                    evalMap[id] = evaluation;
                }
            });
            setSessionEvaluations(evalMap);
        } catch (err) {
            console.error('Failed to load session evaluations:', err);
        }
    };

    const loadEvaluationForCandidate = async (id: string) => {
        if (evaluations[id]) return;
        try {
            const authHeaders = await getAuthHeaders();
            const response = await axios.get(`${API_BASE}/candidates/${id}/evaluation`, { headers: authHeaders });
            if (response.data.data) {
                setEvaluations(prev => ({ ...prev, [id]: response.data.data }));
            }
        } catch (err) {
            console.error(`Failed to load evaluation for candidate ${id}:`, err);
        }
    };

    const toggleCandidate = async (id: string) => {
        if (selectedCandidates.includes(id)) {
            if (selectedCandidates.length > 1) {
                setSelectedCandidates(prev => prev.filter(c => c !== id));
            }
        } else {
            if (selectedCandidates.length < 4) {
                const newSelection = [...selectedCandidates, id];
                setSelectedCandidates(newSelection);
                await loadEvaluationForCandidate(id);
            }
        }
    };

    const toggleSession = async (sessionId: string) => {
        if (selectedSessions.includes(sessionId)) {
            if (selectedSessions.length > 1) {
                const newSelection = selectedSessions.filter(s => s !== sessionId);
                setSelectedSessions(newSelection);
            } else {
                setSelectedSessions([]);
            }
        } else {
            if (selectedSessions.length < 4) {
                const newSelection = [...selectedSessions, sessionId];
                setSelectedSessions(newSelection);
                
                // Load evaluation for the new session if not already loaded
                if (!sessionEvaluations[sessionId]) {
                    try {
                        const authHeaders = await getAuthHeaders();
                        const response = await axios.get(`${API_BASE}/candidates/${sessionId}/evaluation`, { headers: authHeaders });
                        if (response.data.data) {
                            setSessionEvaluations(prev => ({ ...prev, [sessionId]: response.data.data }));
                        }
                    } catch (err) {
                        console.error(`Failed to load evaluation for session ${sessionId}:`, err);
                    }
                }
            }
        }
    };

    const selectCandidateForSessions = (candidateId: string) => {
        setSelectedCandidateForSessions(candidateId);
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const comparisonData = comparisonMode === 'candidates' 
        ? (() => {
            const nameCount: Record<string, number> = {};
            selectedCandidates.forEach(id => {
                const candidate = candidates.find(c => c.id === id);
                const name = candidate?.name || 'Unknown';
                nameCount[name] = (nameCount[name] || 0) + 1;
            });
            const nameOccurrence: Record<string, number> = {};
            return selectedCandidates.map((id, index) => {
                const candidate = candidates.find(c => c.id === id);
                const evaluation = evaluations[id];
                const name = candidate?.name || 'Unknown';
                nameOccurrence[name] = (nameOccurrence[name] || 0) + 1;
                const label = nameCount[name] > 1 
                    ? `${name} (${nameOccurrence[name]})` 
                    : name;
                return { 
                    candidate, 
                    evaluation, 
                    label,
                    uniqueKey: id,
                    index,
                    session: undefined as typeof candidateSessions[0] | undefined 
                };
            }).filter(d => d.candidate);
        })()
        : selectedSessions.map((sessionId, index) => {
            const session = candidateSessions.find(s => s.id === sessionId);
            const evaluation = sessionEvaluations[sessionId];
            const candidate = candidates.find(c => c.id === selectedCandidateForSessions);
            const sessionDate = session?.completed_at ? formatDate(session.completed_at) : '';
            const title = session?.simulation_title || 'Session';
            return { 
                candidate, 
                evaluation, 
                session,
                label: sessionDate ? `${title} (${sessionDate})` : `Session ${index + 1}`,
                uniqueKey: sessionId,
                index
            };
        }).filter(d => d.session);

    const getRadarData = () => {
        if (comparisonData.length === 0) return [];

        const allSkills = new Set<string>();
        comparisonData.forEach(({ evaluation }) => {
            const skills = getSkillEvaluations(evaluation);
            skills.forEach(skill => {
                allSkills.add(skill.skill);
            });
        });

        return Array.from(allSkills).map(skillName => {
            const dataPoint: any = { 
                skill: skillName.length > 15 ? skillName.substring(0, 15) + '...' : skillName,
                fullSkillName: skillName
            };
            comparisonData.forEach(({ uniqueKey, evaluation }) => {
                const skills = getSkillEvaluations(evaluation);
                const skill = skills.find(s => s.skill === skillName);
                dataPoint[uniqueKey] = skill?.score || 0;
            });
            return dataPoint;
        });
    };

    const getCompetencyRadarData = () => {
        if (comparisonData.length === 0) return [];

        const allCompetencies = new Set<string>();
        comparisonData.forEach(({ evaluation }) => {
            const clusters = evaluation?.aggregated_skill_clusters || [];
            clusters.forEach(cluster => {
                allCompetencies.add(cluster.skill);
            });
        });

        if (allCompetencies.size === 0) return [];

        return Array.from(allCompetencies).map(competencyName => {
            const dataPoint: any = { 
                skill: competencyName.length > 15 ? competencyName.substring(0, 15) + '...' : competencyName,
                fullSkillName: competencyName
            };
            comparisonData.forEach(({ uniqueKey, evaluation }) => {
                const clusters = evaluation?.aggregated_skill_clusters || [];
                const cluster = clusters.find(c => c.skill === competencyName);
                dataPoint[uniqueKey] = cluster?.score || 0;
            });
            return dataPoint;
        });
    };

    const radarData = getRadarData();
    const competencyRadarData = getCompetencyRadarData();

    const metrics = [
        { label: 'Overall Score', key: 'overall_score' },
        { label: 'Objectives Completed', key: 'completed_objectives' },
        { label: 'Total Interactions', key: 'total_turns' },
        { label: 'Overall Progress', key: 'overall_progress' },
    ];

    const getMetricValue = (metric: string, evaluation: CandidateEvaluation | undefined) => {
        if (!evaluation) return 'N/A';
        if (metric === 'overall_score') return evaluation.overall_score || 'N/A';
        if (metric === 'completed_objectives') {
            const summary = evaluation.simulation_summary;
            return summary ? `${summary.completed_objectives}/${summary.total_objectives}` : 'N/A';
        }
        if (metric === 'total_turns') return evaluation.simulation_summary?.total_turns || 'N/A';
        if (metric === 'overall_progress') {
            const progress = evaluation.simulation_summary?.overall_progress;
            return progress !== undefined ? `${Math.round(progress * 100)}%` : 'N/A';
        }
        return 'N/A';
    };

    const getBestPerformer = (metric: string) => {
        let bestIndex = 0;
        let bestValue = -Infinity;

        comparisonData.forEach(({ evaluation }, index) => {
            if (!evaluation) return;
            let value = 0;
            if (metric === 'overall_score') value = evaluation.overall_score || 0;
            if (metric === 'completed_objectives') value = evaluation.simulation_summary?.completed_objectives || 0;
            if (metric === 'total_turns') value = evaluation.simulation_summary?.total_turns || 0;
            if (metric === 'overall_progress') value = evaluation.simulation_summary?.overall_progress || 0;

            if (value > bestValue) {
                bestValue = value;
                bestIndex = index;
            }
        });

        return bestIndex;
    };

    const getInitials = (name: string) => {
        const parts = name.split(' ').filter(Boolean);
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    };

    const getAvatarColor = (name: string) => {
        const colors = [
            'bg-blue-500', 'bg-emerald-500', 'bg-purple-500',
            'bg-orange-500', 'bg-pink-500', 'bg-cyan-500'
        ];
        const index = name.charCodeAt(0) % colors.length;
        return colors[index];
    };

    const uniqueCandidates = candidates.reduce((acc, candidate) => {
        const userId = candidate.user_id || candidate.id;
        if (!acc.some(c => (c.user_id || c.id) === userId)) {
            acc.push(candidate);
        }
        return acc;
    }, [] as Candidate[]);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50">
                <AppHeader activePage="comparison" />
                <div className="flex items-center justify-center h-[calc(100vh-64px)]">
                    <div className="text-center">
                        <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-slate-600">Loading comparison data...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <AppHeader activePage="comparison" />

            <main className="max-w-7xl mx-auto px-6 py-8">
                <div className="space-y-6">
                    {/* Page Header */}
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 mb-2">
                            {comparisonMode === 'candidates' ? 'Candidate Comparison' : 'Session Comparison'}
                        </h1>
                        <p className="text-slate-600">
                            {comparisonMode === 'candidates' 
                                ? 'Compare performance across multiple candidates side by side'
                                : 'Compare multiple simulation sessions for a single candidate'}
                        </p>
                    </div>

                    {/* Mode Selector - Pill Toggle */}
                    <div className="flex justify-center">
                        <div className="bg-slate-100 rounded-full p-1 inline-flex relative">
                            {/* Sliding indicator */}
                            <div 
                                className={clsx(
                                    'absolute top-1 bottom-1 rounded-full bg-emerald-500 shadow-md transition-all duration-300 ease-out',
                                    comparisonMode === 'candidates' ? 'left-1 w-[calc(50%-2px)]' : 'left-[calc(50%+1px)] w-[calc(50%-2px)]'
                                )}
                            />
                            <button
                                onClick={() => {
                                    setComparisonMode('candidates');
                                    setSelectedCandidateForSessions(null);
                                    setSelectedSessions([]);
                                }}
                                className={clsx(
                                    'relative z-10 flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-colors duration-300',
                                    comparisonMode === 'candidates'
                                        ? 'text-white'
                                        : 'text-slate-600 hover:text-slate-900'
                                )}
                            >
                                <Users className="w-4 h-4" />
                                <span>Compare Candidates</span>
                            </button>
                            <button
                                onClick={() => {
                                    setComparisonMode('sessions');
                                    setSelectedCandidates([]);
                                }}
                                className={clsx(
                                    'relative z-10 flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-colors duration-300',
                                    comparisonMode === 'sessions'
                                        ? 'text-white'
                                        : 'text-slate-600 hover:text-slate-900'
                                )}
                            >
                                <Clock className="w-4 h-4" />
                                <span>Compare Sessions</span>
                            </button>
                        </div>
                    </div>

                    {/* Selection UI based on mode */}
                    {comparisonMode === 'candidates' ? (
                        <div className="bg-white rounded-xl border border-slate-200 p-6">
                            <h3 className="font-semibold text-slate-900 mb-4">Select Candidates to Compare (Max 4)</h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                {uniqueCandidates.map(candidate => (
                                    <button
                                        key={candidate.id}
                                        onClick={() => toggleCandidate(candidate.id)}
                                        disabled={!selectedCandidates.includes(candidate.id) && selectedCandidates.length >= 4}
                                        className={clsx(
                                            'p-4 rounded-lg border-2 transition-all duration-200 text-left',
                                            selectedCandidates.includes(candidate.id)
                                                ? 'border-emerald-500 bg-emerald-50'
                                                : 'border-slate-200 hover:border-slate-300 bg-white',
                                            !selectedCandidates.includes(candidate.id) && selectedCandidates.length >= 4 && 'opacity-50 cursor-not-allowed'
                                        )}
                                    >
                                        <div className="flex items-center gap-3 mb-2">
                                            {candidate.image_url ? (
                                                <img
                                                    src={candidate.image_url}
                                                    alt={candidate.name}
                                                    className="w-10 h-10 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className={clsx(
                                                    'w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm',
                                                    getAvatarColor(candidate.name)
                                                )}>
                                                    {getInitials(candidate.name)}
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-slate-900 text-sm truncate">{candidate.name}</div>
                                                <div className="text-xs text-slate-500">
                                                    Score: {candidate.average_score?.toFixed(0) || 'N/A'}
                                                </div>
                                            </div>
                                            {selectedCandidates.includes(candidate.id) && (
                                                <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                                                    <Check className="w-3 h-3 text-white" />
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                            {uniqueCandidates.length === 0 && (
                                <div className="text-center py-8 text-slate-500">
                                    No completed candidates available for comparison.
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Step 1: Select Candidate - Dropdown */}
                            <div className="bg-white rounded-xl border border-slate-200 p-6">
                                <h3 className="font-semibold text-slate-900 mb-4">Step 1: Select a Candidate</h3>
                                <div className="relative max-w-md">
                                    <select
                                        value={selectedCandidateForSessions || ''}
                                        onChange={(e) => selectCandidateForSessions(e.target.value)}
                                        className="w-full px-4 py-3 pr-10 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white appearance-none text-slate-900 font-medium"
                                    >
                                        <option value="" disabled>Select a candidate...</option>
                                        {uniqueCandidates.map(candidate => (
                                            <option key={candidate.id} value={candidate.id}>
                                                {candidate.name} - {candidate.email}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                                </div>
                                {uniqueCandidates.length === 0 && (
                                    <div className="text-center py-8 text-slate-500">
                                        No completed candidates available.
                                    </div>
                                )}
                            </div>

                            {/* Step 2: Select Sessions - Scrollable */}
                            {selectedCandidateForSessions && (
                                <div className="bg-white rounded-xl border border-slate-200 p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="font-semibold text-slate-900">Step 2: Select Sessions to Compare (Max 4)</h3>
                                        {selectedSessions.length > 0 && (
                                            <span className="text-sm text-emerald-600 font-medium">
                                                {selectedSessions.length}/4 selected
                                            </span>
                                        )}
                                    </div>
                                    {loadingSessions ? (
                                        <div className="text-center py-8">
                                            <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                                            <p className="text-slate-500 text-sm">Loading sessions...</p>
                                        </div>
                                    ) : candidateSessions.length > 0 ? (
                                        <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg">
                                            <div className="divide-y divide-slate-100">
                                                {candidateSessions.map(session => {
                                                    const sessionIndex = selectedSessions.indexOf(session.id);
                                                    const isSelected = sessionIndex !== -1;
                                                    return (
                                                        <button
                                                            key={session.id}
                                                            onClick={() => toggleSession(session.id)}
                                                            disabled={!isSelected && selectedSessions.length >= 4}
                                                            className={clsx(
                                                                'w-full p-4 transition-all duration-200 text-left flex items-center gap-3',
                                                                isSelected
                                                                    ? 'bg-slate-50'
                                                                    : 'hover:bg-slate-50',
                                                                !isSelected && selectedSessions.length >= 4 && 'opacity-50 cursor-not-allowed'
                                                            )}
                                                        >
                                                            {isSelected ? (
                                                                <div 
                                                                    className="w-6 h-6 rounded-full flex items-center justify-center text-white font-medium text-xs flex-shrink-0"
                                                                    style={{ backgroundColor: CHART_COLORS[sessionIndex % CHART_COLORS.length] }}
                                                                >
                                                                    {sessionIndex + 1}
                                                                </div>
                                                            ) : (
                                                                <div className="w-6 h-6 rounded-full border-2 border-slate-300 flex-shrink-0" />
                                                            )}
                                                            <div className="flex-1 min-w-0">
                                                                <div className="font-medium text-slate-900 text-sm truncate">
                                                                    {session.simulation_title}
                                                                </div>
                                                                <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                                                                    <span>Score: {session.score?.toFixed(0) || 'N/A'}</span>
                                                                    <span>{formatDate(session.completed_at)}</span>
                                                                </div>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-slate-500">
                                            No completed sessions found for this candidate.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Comparison Results */}
                    {comparisonData.length > 0 && (
                        <>
                            {/* Objectives Comparison Radar Chart */}
                            <div className="bg-white rounded-xl border border-slate-200 p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-semibold text-slate-900">Objectives Comparison</h3>
                                    {/* Color Legend */}
                                    {radarData.length > 0 && (
                                        <div className="flex items-center gap-4 flex-wrap">
                                            {comparisonData.map(({ label, session, candidate }, index) => (
                                                <div key={`legend-${index}`} className="flex items-center gap-2">
                                                    <div 
                                                        className="w-3 h-3 rounded-full"
                                                        style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                                                    />
                                                    <span className="text-xs text-slate-600 font-medium truncate max-w-[120px]">
                                                        {comparisonMode === 'sessions' 
                                                            ? (session?.simulation_title || label)
                                                            : (candidate?.name || label)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {radarData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={400}>
                                        <RadarChart data={radarData}>
                                            <PolarGrid stroke="#e2e8f0" />
                                            <PolarAngleAxis dataKey="skill" tick={{ fill: '#64748b', fontSize: 11 }} />
                                            <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                                            {comparisonData.map(({ label, uniqueKey }, index) => (
                                                <Radar
                                                    key={uniqueKey}
                                                    name={label}
                                                    dataKey={uniqueKey}
                                                    stroke={CHART_COLORS[index % CHART_COLORS.length]}
                                                    strokeWidth={2}
                                                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                                                    fillOpacity={0.25}
                                                />
                                            ))}
                                            <Tooltip 
                                                content={({ active, payload }) => {
                                                    if (!active || !payload || payload.length === 0) return null;
                                                    const fullSkillName = payload[0]?.payload?.fullSkillName || payload[0]?.payload?.skill;
                                                    return (
                                                        <div className="bg-slate-800 text-slate-100 rounded-lg px-4 py-3 shadow-xl max-w-xs">
                                                            <p className="font-medium text-sm mb-2 text-white">{fullSkillName}</p>
                                                            <div className="space-y-1">
                                                                {payload.map((entry: any, index: number) => (
                                                                    <div key={index} className="flex items-center justify-between gap-4 text-sm">
                                                                        <span className="flex items-center gap-2">
                                                                            <span 
                                                                                className="w-2.5 h-2.5 rounded-full" 
                                                                                style={{ backgroundColor: entry.color }}
                                                                            />
                                                                            <span className="text-slate-300">{entry.name}</span>
                                                                        </span>
                                                                        <span className="font-medium">{entry.value?.toFixed(1)}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                            />
                                            <Legend 
                                                wrapperStyle={{ paddingTop: '20px' }}
                                                formatter={(value) => <span className="text-slate-700 text-sm">{value}</span>}
                                            />
                                        </RadarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="flex items-center justify-center h-[300px] text-slate-500">
                                        <div className="text-center">
                                            {comparisonData.some(d => d.evaluation) ? (
                                                <>
                                                    <svg className="w-16 h-16 mx-auto mb-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                    </svg>
                                                    <p className="font-medium">No skill evaluation data available</p>
                                                    <p className="text-sm text-slate-400 mt-1">The selected items don't have detailed skill breakdowns</p>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="w-16 h-16 border-4 border-slate-200 border-t-emerald-500 rounded-full animate-spin mx-auto mb-4"></div>
                                                    <p>Loading skill evaluations...</p>
                                                    <p className="text-sm text-slate-400 mt-1">Skills data will appear once evaluations are loaded</p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Skills Comparison Radar Chart */}
                            {competencyRadarData.length > 0 && (
                                <div className="bg-white rounded-xl border border-slate-200 p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h3 className="font-semibold text-slate-900">Skills Comparison</h3>
                                            <p className="text-sm text-slate-500 mt-1">Aggregated skill scores across all objectives</p>
                                        </div>
                                        {/* Color Legend */}
                                        <div className="flex items-center gap-4 flex-wrap">
                                            {comparisonData.map(({ label, session, candidate }, index) => (
                                                <div key={`competency-legend-${index}`} className="flex items-center gap-2">
                                                    <div 
                                                        className="w-3 h-3 rounded-full"
                                                        style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                                                    />
                                                    <span className="text-xs text-slate-600 font-medium truncate max-w-[120px]">
                                                        {comparisonMode === 'sessions' 
                                                            ? (session?.simulation_title || label)
                                                            : (candidate?.name || label)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <ResponsiveContainer width="100%" height={400}>
                                        <RadarChart data={competencyRadarData}>
                                            <PolarGrid stroke="#e2e8f0" />
                                            <PolarAngleAxis dataKey="skill" tick={{ fill: '#64748b', fontSize: 11 }} />
                                            <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                                            {comparisonData.map(({ label, uniqueKey }, index) => (
                                                <Radar
                                                    key={`competency-${uniqueKey}`}
                                                    name={label}
                                                    dataKey={uniqueKey}
                                                    stroke={CHART_COLORS[index % CHART_COLORS.length]}
                                                    strokeWidth={2}
                                                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                                                    fillOpacity={0.25}
                                                />
                                            ))}
                                            <Tooltip 
                                                content={({ active, payload }) => {
                                                    if (!active || !payload || payload.length === 0) return null;
                                                    const fullSkillName = payload[0]?.payload?.fullSkillName || payload[0]?.payload?.skill;
                                                    return (
                                                        <div className="bg-slate-800 text-slate-100 rounded-lg px-4 py-3 shadow-xl max-w-xs">
                                                            <p className="font-medium text-sm mb-2 text-white">{fullSkillName}</p>
                                                            <div className="space-y-1">
                                                                {payload.map((entry: any, index: number) => (
                                                                    <div key={index} className="flex items-center justify-between gap-4 text-sm">
                                                                        <span className="flex items-center gap-2">
                                                                            <span 
                                                                                className="w-2.5 h-2.5 rounded-full" 
                                                                                style={{ backgroundColor: entry.color }}
                                                                            />
                                                                            <span className="text-slate-300">{entry.name}</span>
                                                                        </span>
                                                                        <span className="font-medium">{entry.value?.toFixed(1)}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                            />
                                            <Legend 
                                                wrapperStyle={{ paddingTop: '20px' }}
                                                formatter={(value) => <span className="text-slate-700 text-sm">{value}</span>}
                                            />
                                        </RadarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Skills Scores Comparison Table */}
                            {competencyRadarData.length > 0 && (
                                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-slate-50 border-b border-slate-200">
                                                <tr>
                                                    <th className="text-left p-4 font-semibold text-slate-900 sticky left-0 bg-slate-50 z-10">
                                                        Skill
                                                    </th>
                                                    {comparisonData.map(({ candidate, label, session }, index) => (
                                                        <th key={`skill-header-${index}`} className="text-center p-4">
                                                            <div className="flex flex-col items-center gap-2">
                                                                {comparisonMode === 'candidates' && candidate ? (
                                                                    <>
                                                                        {candidate.image_url ? (
                                                                            <img
                                                                                src={candidate.image_url}
                                                                                alt={candidate.name}
                                                                                className="w-12 h-12 rounded-full object-cover"
                                                                            />
                                                                        ) : (
                                                                            <div className={clsx(
                                                                                'w-12 h-12 rounded-full flex items-center justify-center text-white font-medium',
                                                                                getAvatarColor(candidate.name)
                                                                            )}>
                                                                                {getInitials(candidate.name)}
                                                                            </div>
                                                                        )}
                                                                        <div>
                                                                            <div className="font-semibold text-slate-900">{candidate.name}</div>
                                                                            <div className="text-xs text-slate-500">{candidate.position_applied || candidate.email}</div>
                                                                        </div>
                                                                    </>
                                                                ) : session ? (
                                                                    <div>
                                                                        <div 
                                                                            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-medium mx-auto mb-2"
                                                                            style={{ 
                                                                                backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                                                                                boxShadow: `0 0 0 2px white, 0 0 0 6px ${CHART_COLORS[index % CHART_COLORS.length]}40`
                                                                            }}
                                                                        >
                                                                            {index + 1}
                                                                        </div>
                                                                        <div className="font-semibold text-slate-900">{session.simulation_title}</div>
                                                                        <div className="text-xs text-slate-500">{formatDate(session.completed_at)}</div>
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-200">
                                                {competencyRadarData.map((competency, compIdx) => {
                                                    const scores = comparisonData.map(({ uniqueKey }) => competency[uniqueKey] || 0);
                                                    const maxScore = Math.max(...scores);

                                                    return (
                                                        <tr key={compIdx} className="hover:bg-slate-50 transition-colors">
                                                            <td className="p-4 font-medium text-slate-900 sticky left-0 bg-white hover:bg-slate-50 z-10">
                                                                {competency.fullSkillName || competency.skill}
                                                            </td>
                                                            {comparisonData.map(({ uniqueKey }, index) => {
                                                                const score = competency[uniqueKey] || 0;
                                                                const isBest = score === maxScore && maxScore > 0;

                                                                return (
                                                                    <td key={`skill-${compIdx}-${index}`} className="text-center p-4">
                                                                        <div className={clsx(
                                                                            'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg font-semibold',
                                                                            isBest ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500' : 'bg-slate-100 text-slate-700'
                                                                        )}>
                                                                            {score.toFixed(1)}
                                                                        </div>
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Metrics Comparison Table */}
                            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-slate-50 border-b border-slate-200">
                                            <tr>
                                                <th className="text-left p-4 font-semibold text-slate-900 sticky left-0 bg-slate-50 z-10">
                                                    Metric
                                                </th>
                                                {comparisonData.map(({ candidate, label, session }, index) => (
                                                    <th key={`header-${index}`} className="text-center p-4">
                                                        <div className="flex flex-col items-center gap-2">
                                                            {comparisonMode === 'candidates' && candidate ? (
                                                                <>
                                                                    {candidate.image_url ? (
                                                                        <img
                                                                            src={candidate.image_url}
                                                                            alt={candidate.name}
                                                                            className="w-12 h-12 rounded-full object-cover"
                                                                        />
                                                                    ) : (
                                                                        <div className={clsx(
                                                                            'w-12 h-12 rounded-full flex items-center justify-center text-white font-medium',
                                                                            getAvatarColor(candidate.name)
                                                                        )}>
                                                                            {getInitials(candidate.name)}
                                                                        </div>
                                                                    )}
                                                                    <div>
                                                                        <div className="font-semibold text-slate-900">{candidate.name}</div>
                                                                        <div className="text-xs text-slate-500">{candidate.position_applied || candidate.email}</div>
                                                                    </div>
                                                                </>
                                                            ) : session ? (
                                                                <div>
                                                                    <div 
                                                                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-medium mx-auto mb-2"
                                                                        style={{ 
                                                                            backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                                                                            boxShadow: `0 0 0 2px white, 0 0 0 6px ${CHART_COLORS[index % CHART_COLORS.length]}40`
                                                                        }}
                                                                    >
                                                                        {index + 1}
                                                                    </div>
                                                                    <div className="font-semibold text-slate-900">{session.simulation_title}</div>
                                                                    <div className="text-xs text-slate-500">{formatDate(session.completed_at)}</div>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200">
                                            {metrics.map(metric => {
                                                const bestIndex = getBestPerformer(metric.key);
                                                return (
                                                    <tr key={metric.key} className="hover:bg-slate-50 transition-colors">
                                                        <td className="p-4 font-medium text-slate-900 sticky left-0 bg-white hover:bg-slate-50 z-10">
                                                            {metric.label}
                                                        </td>
                                                        {comparisonData.map(({ evaluation }, index) => {
                                                            const isBest = index === bestIndex;
                                                            return (
                                                                <td key={`metric-${index}`} className="text-center p-4">
                                                                    <div className={clsx(
                                                                        'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg font-semibold',
                                                                        isBest ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500' : 'bg-slate-100 text-slate-700'
                                                                    )}>
                                                                        {getMetricValue(metric.key, evaluation)}
                                                                    </div>
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                );
                                            })}

                                            {/* Objectives breakdown */}
                                            {getSkillEvaluations(comparisonData[0]?.evaluation).length > 0 && (
                                                <>
                                                    <tr className="bg-slate-50 font-semibold">
                                                        <td className="p-4 sticky left-0 bg-slate-50 z-10">Individual Objectives</td>
                                                        {comparisonData.map((_, index) => (
                                                            <td key={`skill-header-${index}`} className="text-center p-4 text-slate-600 text-sm">
                                                                Score
                                                            </td>
                                                        ))}
                                                    </tr>

                                                    {getSkillEvaluations(comparisonData[0].evaluation).map((skill, skillIndex) => {
                                                        const skillName = skill.skill;
                                                        const scores = comparisonData.map(({ evaluation }) => {
                                                            const skills = getSkillEvaluations(evaluation);
                                                            const s = skills.find(sk => sk.skill === skillName);
                                                            return s?.score || 0;
                                                        });
                                                        const maxScore = Math.max(...scores);

                                                        return (
                                                            <tr key={skillIndex} className="hover:bg-slate-50 transition-colors">
                                                                <td className="p-4 text-sm text-slate-700 sticky left-0 bg-white hover:bg-slate-50 z-10">
                                                                    {skillName}
                                                                </td>
                                                                {comparisonData.map(({ evaluation }, index) => {
                                                                    const skills = getSkillEvaluations(evaluation);
                                                                    const skillData = skills.find(s => s.skill === skillName);
                                                                    const score = skillData?.score || 0;
                                                                    const isBest = score === maxScore && maxScore > 0;

                                                                    return (
                                                                        <td key={`skill-${skillIndex}-${index}`} className="text-center p-4">
                                                                            <div className={clsx(
                                                                                'inline-flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-medium',
                                                                                isBest ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'
                                                                            )}>
                                                                                {score.toFixed(1)}
                                                                            </div>
                                                                        </td>
                                                                    );
                                                                })}
                                                            </tr>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Summary Recommendations */}
                            <div className="bg-white rounded-xl border border-slate-200 p-6">
                                <h3 className="font-semibold text-slate-900 mb-4">Summary</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {comparisonData.map(({ candidate, evaluation, label, session }, index) => (
                                        <div key={`summary-${index}`} className="border border-slate-200 rounded-lg p-4">
                                            <div className="flex items-center gap-3 mb-3">
                                                {comparisonMode === 'candidates' && candidate ? (
                                                    <>
                                                        {candidate.image_url ? (
                                                            <img
                                                                src={candidate.image_url}
                                                                alt={candidate.name}
                                                                className="w-10 h-10 rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className={clsx(
                                                                'w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm',
                                                                getAvatarColor(candidate.name)
                                                            )}>
                                                                {getInitials(candidate.name)}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div className="font-semibold text-slate-900">{candidate.name}</div>
                                                            <div className="text-sm text-slate-600">
                                                                Overall: {evaluation?.overall_score || candidate.average_score?.toFixed(0) || 'N/A'}/100
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : session ? (
                                                    <>
                                                        <div 
                                                            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm"
                                                            style={{ 
                                                                backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                                                                boxShadow: `0 0 0 1px white, 0 0 0 3px ${CHART_COLORS[index % CHART_COLORS.length]}60`
                                                            }}
                                                        >
                                                            {index + 1}
                                                        </div>
                                                        <div>
                                                            <div className="font-semibold text-slate-900">{session.simulation_title}</div>
                                                            <div className="text-sm text-slate-600">
                                                                Overall: {evaluation?.overall_score || session.score?.toFixed(0) || 'N/A'}/100
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : null}
                                            </div>
                                            {(() => {
                                                const skills = getSkillEvaluations(evaluation);
                                                return skills.length > 0 && (
                                                    <div className="space-y-2">
                                                        <div>
                                                            <div className="text-xs font-semibold text-slate-700 mb-1">Key Strengths:</div>
                                                            <div className="text-sm text-slate-600">
                                                                {[...skills]
                                                                    .sort((a, b) => b.score - a.score)
                                                                    .slice(0, 2)
                                                                    .map(s => s.skill)
                                                                    .join(', ')}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div className="text-xs font-semibold text-slate-700 mb-1">Areas to Develop:</div>
                                                            <div className="text-sm text-slate-600">
                                                                {[...skills]
                                                                    .sort((a, b) => a.score - b.score)
                                                                    .slice(0, 2)
                                                                    .map(s => s.skill)
                                                                    .join(', ')}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {comparisonData.length === 0 && !loading && (
                        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                            <p className="text-slate-600">
                                {comparisonMode === 'candidates' 
                                    ? 'Select at least one candidate to view comparison.'
                                    : selectedCandidateForSessions 
                                        ? 'Select at least one session to view comparison.'
                                        : 'Select a candidate first, then choose sessions to compare.'}
                            </p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
